import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle, Star, Briefcase } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: "initial",
  whileInView: "animate",
  viewport: { once: true },
  variants: {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

const pricingPlansData = [
  { 
    name: "Free", 
    price: "$0", 
    period: "/month", 
    credits: "100 credits/month",
    features: [
      "Access to core tools for basic PDF workflows",
      "Max file size: 5 MB",
      "Public download URLs only",
      "No support"
    ],
    popular: false,
    ctaText: "Start Free",
    ctaLink: "https://crafter.webfiny.com/",
    ariaLabel: "Start Free with WebFiny Crafter"
  },
  { 
    name: "Developer", 
    price: "$9", 
    period: "/month", 
    credits: "1,000 credits/month",
    features: [
      "Full access to all API features",
      "Max file size: 100 MB",
      "Signed URLs enabled",
      "Email support (72hr SLA)"
    ],
    popular: true,
    ctaText: "Get Started",
    ctaLink: "https://crafter.webfiny.com/",
    ariaLabel: "Get Started with Developer Plan on WebFiny Crafter"
  },
  { 
    name: "Business", 
    price: "$29", 
    period: "/month", 
    credits: "10,000 credits/month",
    features: [
      "Advanced features, scalable usage",
      "Max file size: 512 MB",
      "Signed + dedicated storage",
      "Priority support (24hr SLA)"
    ],
    popular: false,
    ctaText: "Choose Plan",
    ctaLink: "https://crafter.webfiny.com/",
    ariaLabel: "Choose Business Plan on WebFiny Crafter"
  },
  {
    name: "Enterprise",
    price: "Starts at $99",
    period: "/month",
    credits: "Custom credits, hosting, and SLA",
    features: [
      "Tailored for high-volume & specific needs",
      "Dedicated infrastructure options",
      "Custom feature development",
      "Premium support & account management"
    ],
    popular: false,
    ctaText: "Contact Sales",
    ctaLink: "mailto:info@webfiny.com?subject=Enterprise Plan Inquiry",
    isEnterprise: true,
    ariaLabel: "Contact Sales for Enterprise Plan"
  }
];


const PricingSection = ({ onActionClick }) => {
  const handleCtaClick = (link, text, isEnterprise = false, ariaLabel) => {
    if (isEnterprise) {
      if (link.startsWith('mailto:')) {
        window.location.href = link;
      } else if (link.startsWith('http')) {
         window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        onActionClick(text);
      }
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section id="pricing" className="py-20 bg-background border-t border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-primary-foreground">
            Simple, Credit-Based <span className="gradient-text-brand">Pricing</span>
          </h2>
          <p className="text-xl text-foreground max-w-3xl mx-auto">
            Choose the plan that fits your needs. Scale with confidence.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
          {...staggerContainer}
        >
          {pricingPlansData.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative bg-card p-6 sm:p-8 rounded-xl shadow-2xl hover-lift flex flex-col border ${
                plan.popular ? 'border-primary' : 'border-border'
              } ${plan.isEnterprise ? 'lg:col-span-1 md:col-span-2' : 'lg:col-span-1 md:col-span-1'}`}
              variants={fadeInUp}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="whitespace-nowrap bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold flex items-center shadow-lg">
                    <Star className="h-4 w-4 mr-1.5 fill-current text-secondary" /> Most Popular
                  </span>
                </div>
              )}
              
              <div className="flex-grow">
                <h3 className="text-2xl font-bold mb-2 text-card-foreground font-heading">{plan.name}</h3>
                 <div className="mb-4">
                  <span className="text-4xl font-bold text-card-foreground">{plan.price}</span>
                  <span className="text-muted-foreground text-sm">{plan.period}</span>
                </div>
                <p className="text-base text-primary font-semibold mb-6">{plan.credits}</p>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <Button 
                onClick={() => handleCtaClick(plan.ctaLink, `${plan.ctaText} for ${plan.name}`, plan.isEnterprise, plan.ariaLabel)}
                className={`w-full mt-auto shadow-md ${
                  plan.popular ? 'bg-primary text-primary-foreground hover:bg-primary/80 pulse-glow-brand' : 
                  plan.isEnterprise ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80' : 'bg-primary/10 text-primary hover:bg-primary/20'
                }`}
                size="lg"
                aria-label={plan.ariaLabel}
              >
                {plan.isEnterprise && <Briefcase className="h-5 w-5 mr-2" />}
                {plan.ctaText}
              </Button>
              {plan.name === "Free" && <p className="text-xs text-muted-foreground text-center mt-3">No credit card required.</p>}
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p 
          className="text-center text-foreground mt-10 text-sm"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          All plans are billed monthly. You can upgrade, downgrade, or cancel anytime.
        </motion.p>
      </div>
    </section>
  );
};

export default PricingSection;